import { v4 as uuidv4 } from "uuid";

import { isHexColor } from "@/lib/colors/helpers/converters";
import { DEFAULT_THEME, mixHex } from "./theme";
import { normalizeWebUrl } from "./webUrl";

import {
    AccordionAppearance,
    AccordionItem,
    BUTTON_ALIGNS,
    BUTTON_SIZES,
    BUTTON_STYLES,
    ButtonAlign,
    ButtonAppearance,
    ButtonBlock,
    ButtonSize,
    ButtonStyle,
    ChartAppearance,
    ChartPoint,
    ChartVariant,
    ChecklistAppearance,
    ChecklistItem,
    DIVIDER_SPACINGS,
    DIVIDER_THICKNESSES,
    DividerAppearance,
    DividerSpacing,
    DividerThickness,
    EmbedProvider,
    FlipCard,
    FlipCardsAppearance,
    FlipCardsBlock,
    FlipCardsVariant,
    HotspotMarker,
    HowToStep,
    LayoutColumn,
    LomdaTheme,
    LomdaV3Block,
    LomdaV3BlockType,
    PollAppearance,
    PollOption,
    QuizAppearance,
    ResolvedQuizAppearance,
    QuizOption,
    QuizQuestion,
    QuizQuestionType,
    MediaCell,
    MediaKind,
    MediaPosition,
    ScenarioChoice,
    StepsAppearance,
    TableAppearance,
    TabsAppearance,
    ScenarioQuality,
    ScenarioScene,
    SortCard,
    SortCategory,
    StepItem,
    TabItem,
    TextColumn,
    TextData,
    TextVariant,
    WalkthroughHotspot,
    WalkthroughStep,
    WalkthroughStepType,
} from "@/lib/utils/lomdaV3/types";

const emptyTextColumn = (): TextColumn => ({ heading: "", text: "" });

export const emptyMediaCell = (): MediaCell => ({
    url: "",
    alt: "",
    videoId: "",
    name: "",
    thumbnailUrl: "",
    heading: "",
    text: "",
});

export const MEDIA_LAYOUTS: Record<
    MediaKind,
    { key: string; position: MediaPosition; count: number; labelKey: string }[]
> = {
    image: [
        { key: "single", position: "none", count: 1, labelKey: "lomda-v3-editor.media-variant.single-image" },
        { key: "top", position: "top", count: 1, labelKey: "lomda-v3-editor.media-variant.image-top" },
        { key: "bottom", position: "bottom", count: 1, labelKey: "lomda-v3-editor.media-variant.image-bottom" },
    ],
    video: [
        { key: "single", position: "none", count: 1, labelKey: "lomda-v3-editor.media-variant.single-video" },
        { key: "top", position: "top", count: 1, labelKey: "lomda-v3-editor.media-variant.video-top" },
        { key: "bottom", position: "bottom", count: 1, labelKey: "lomda-v3-editor.media-variant.video-bottom" },
        { key: "two", position: "top", count: 2, labelKey: "lomda-v3-editor.media-variant.two-videos" },
        { key: "left", position: "start", count: 1, labelKey: "lomda-v3-editor.media-variant.video-left" },
        { key: "right", position: "end", count: 1, labelKey: "lomda-v3-editor.media-variant.video-right" },
    ],
};

export function resizeMediaCells(cells: MediaCell[], count: number): MediaCell[] {
    const next = cells.slice(0, count);
    while (next.length < count) next.push(emptyMediaCell());
    return next;
}

// Static Tailwind classes (literal so they survive purge) for dynamic columns.
const GRID_COLS: Record<number, string> = {
    1: "",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    5: "md:grid-cols-5",
};
export const gridColsClass = (n: number) => GRID_COLS[n] ?? "md:grid-cols-4";

export function defaultTableRows(): string[][] {
    return [
        ["", "", ""],
        ["", "", ""],
        ["", "", ""],
    ];
}

/** Parse a legacy `<table>` HTML blob into a rows grid (back-compat only). */
function parseTableHtml(html: string): { rows: string[][]; headerRow: boolean } {
    const rows: string[][] = [];
    let headerRow = false;
    const trMatches = html.match(/<tr[^>]*>[\s\S]*?<\/tr>/gi) ?? [];
    trMatches.forEach((tr, i) => {
        const cells: string[] = [];
        const cellMatches =
            tr.match(/<(?:th|td)[^>]*>[\s\S]*?<\/(?:th|td)>/gi) ?? [];
        cellMatches.forEach((cell) => {
            if (i === 0 && /^<th/i.test(cell)) headerRow = true;
            cells.push(
                cell
                    .replace(/^<(?:th|td)[^>]*>/i, "")
                    .replace(/<\/(?:th|td)>$/i, "")
                    .trim()
            );
        });
        if (cells.length) rows.push(cells);
    });
    if (!rows.length) return { rows: defaultTableRows(), headerRow: true };
    return { rows, headerRow };
}

/** Resolve a table's rows + header flag (handles legacy html + defaults). */
export function tableModel(data: Extract<TextData, { variant: "table" }>): {
    rows: string[][];
    headerRow: boolean;
} {
    if (data.rows && data.rows.length)
        return { rows: data.rows, headerRow: data.headerRow ?? true };
    if (data.html) return parseTableHtml(data.html);
    return { rows: defaultTableRows(), headerRow: true };
}

/** Unset fields follow the theme (body = card, header = muted, border). */
export function resolveTableAppearance(
    a: TableAppearance | undefined,
    theme: LomdaTheme | undefined
): TableAppearance {
    return {
        cardBackground: a?.cardBackground ?? theme?.card ?? DEFAULT_THEME.card,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
        headerBackground:
            a?.headerBackground ?? theme?.muted ?? DEFAULT_THEME.muted,
    };
}

/**
 * Declarative table appearance config. One division — card/header colors, the
 * header-row toggle, and border. Column/row counts are structure (not
 * appearance) and live on the topbar. Mirrors `FLIP_CARDS_CONFIG`.
 */
export const TABLE_CONFIG = {
    maxColumns: 6,
    maxRows: 12,
    appearanceGroups: [
        [
            { key: "cardBackground", labelKey: "lomda-v3-editor.table.appearance-card-bg", control: "color" },
            { key: "headerBackground", labelKey: "lomda-v3-editor.table.appearance-header-bg", control: "color" },
            { key: "headerRow", labelKey: "lomda-v3-editor.table.header", control: "switch" },
            { key: "border", labelKey: "lomda-v3-editor.table.appearance-border", control: "color" },
            { key: "borderWidth", labelKey: "lomda-v3-editor.table.appearance-border-width", control: "borderWidth" },
        ],
    ] as AppearanceField[][],
};

export function createTextBlock(variant: TextVariant): LomdaV3Block {
    const id = uuidv4();
    switch (variant) {
        case "text":
            return { id, type: "text", data: { variant, html: "" } };
        case "heading-text":
            return { id, type: "text", data: { variant, heading: "", text: "" } };
        case "multi-columns":
        case "two-columns":
        case "three-columns":
            return {
                id,
                type: "text",
                data: {
                    variant,
                    columns: [emptyTextColumn(), emptyTextColumn()],
                },
            };
        case "quote":
            return {
                id,
                type: "text",
                data: {
                    variant,
                    quote: "",
                    authorName: "",
                    authorTitle: "",
                    authorPhoto: "",
                    showAuthorPhoto: true,
                    align: "center",
                },
            };
        case "link":
            return {
                id,
                type: "text",
                data: { variant, url: "", title: "", description: "", thumbnail: "" },
            };
        case "table":
            return {
                id,
                type: "text",
                data: {
                    variant,
                    rows: defaultTableRows(),
                    headerRow: true,
                },
            };
    }
}

/** Media width as a percentage of the content area. Legacy blocks stored the
 * sm/md/lg enum — map those so old content keeps rendering. */
export const mediaWidthPct = (w: number | string): number =>
    typeof w === "number"
        ? w
        : ({ sm: 40, md: 70, lg: 100 } as Record<string, number>)[w] ?? 100;

export function createMediaLayout(
    kind: MediaKind,
    position: MediaPosition,
    cellCount: number
): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "media-layout",
        data: {
            kind,
            position,
            width: 100,
            aspect: kind === "video" ? "16:9" : "auto",
            fill: "cover",
            cells: Array.from({ length: cellCount }, emptyMediaCell),
        },
    };
}

export function createAudioBlock(): LomdaV3Block {
    return { id: uuidv4(), type: "audio", data: { url: "", name: "" } };
}

export function createAttachmentBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "attachment",
        data: { url: "", name: "", size: 0 },
    };
}

export function createEmbedBlock(provider: EmbedProvider): LomdaV3Block {
    return { id: uuidv4(), type: "embed", data: { provider, url: "" } };
}

export function createHtmlBlock(): LomdaV3Block {
    return { id: uuidv4(), type: "html", data: { html: "" } };
}

export function createWalkthroughBlock(): LomdaV3Block {
    return { id: uuidv4(), type: "walkthrough", data: { steps: [] } };
}

export function createWalkthroughStep(type: WalkthroughStepType): WalkthroughStep {
    const id = uuidv4();
    if (type === "image") {
        return {
            id,
            type,
            name: "",
            heading: "",
            image: "",
            alt: "",
            aspect: "auto",
            fit: "cover",
            interactive: true,
            hotspots: [],
        };
    }
    return { id, type, name: "", heading: "", text: "", buttons: [] };
}

export const HOTSPOT_COLORS = [
    "#2563eb",
    "#eab308",
    "#ec4899",
    "#22c55e",
    "#f97316",
    "#14b8a6",
];

export const createWalkthroughHotspot = (
    x: number,
    y: number,
    width: number,
    height: number
): WalkthroughHotspot => ({
    id: uuidv4(),
    x,
    y,
    width,
    height,
    label: "",
    color: HOTSPOT_COLORS[0],
    textPosition: "auto",
    linkStepId: "",
});

export const createAccordionItem = (): AccordionItem => ({
    id: uuidv4(),
    title: "",
    body: "",
});

/** Figma default appearance for an accordion block. */
/** Unset fields follow the theme (Figma: card / border) so a brand theme
 *  recolors the block; an author-picked override always wins. */
export function resolveAccordionAppearance(
    a: AccordionAppearance | undefined,
    theme: LomdaTheme | undefined
): AccordionAppearance {
    return {
        cardBackground: a?.cardBackground ?? theme?.card ?? DEFAULT_THEME.card,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
        showCheckmark: a?.showCheckmark ?? true,
    };
}

/**
 * Declarative accordion appearance config. The popover renders from
 * `appearanceGroups` — group 1 = appearance every card-based block has, group 2 =
 * accordion-specific (number color/background, checkmark). Add/remove/reorder a
 * field or group here to change the popover. Mirrors `FLIP_CARDS_CONFIG`.
 */
const ACCORDION_APPEARANCE_GROUPS: AppearanceField[][] = [
    [
        { key: "cardBackground", labelKey: "lomda-v3-editor.accordion.appearance-card-bg", control: "color" },
        { key: "border", labelKey: "lomda-v3-editor.accordion.appearance-border", control: "color" },
        { key: "borderWidth", labelKey: "lomda-v3-editor.accordion.appearance-border-width", control: "borderWidth" },
    ],
    [
        { key: "showCheckmark", labelKey: "lomda-v3-editor.accordion.appearance-checkmark", control: "switch" },
    ],
];
export const ACCORDION_CONFIG = {
    appearanceGroups: ACCORDION_APPEARANCE_GROUPS,
};

export function createAccordionBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "accordion",
        data: {
            title: "",
            description: "",
            items: [
                createAccordionItem(),
                createAccordionItem(),
                createAccordionItem(),
            ],
        },
    };
}

export const createTabItem = (icon = ""): TabItem => ({
    id: uuidv4(),
    label: "",
    icon,
    blocks: [],
});

/** Figma default appearance for a tabs block. */
/** Unset fields follow the theme (card / background / border). */
export function resolveTabsAppearance(
    a: TabsAppearance | undefined,
    theme: LomdaTheme | undefined
): TabsAppearance {
    return {
        tabBackground: a?.tabBackground ?? theme?.card ?? DEFAULT_THEME.card,
        activeTabBackground: a?.activeTabBackground ?? theme?.background ?? DEFAULT_THEME.background,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
        showIcons: a?.showIcons ?? true,
    };
}

/**
 * Declarative tabs appearance config. Division 1 = tab colors/border;
 * division 2 = icon settings. Mirrors `FLIP_CARDS_CONFIG`.
 */
const TABS_APPEARANCE_GROUPS: AppearanceField[][] = [
    [
        { key: "tabBackground", labelKey: "lomda-v3-editor.tabs.appearance-tab-bg", control: "color" },
        { key: "activeTabBackground", labelKey: "lomda-v3-editor.tabs.appearance-active-tab-bg", control: "color" },
        { key: "border", labelKey: "lomda-v3-editor.tabs.appearance-border", control: "color" },
        { key: "borderWidth", labelKey: "lomda-v3-editor.tabs.appearance-border-width", control: "borderWidth" },
    ],
    [
        { key: "showIcons", labelKey: "lomda-v3-editor.tabs.appearance-show-icons", control: "switch" },
    ],
];
export const TABS_CONFIG = {
    appearanceGroups: TABS_APPEARANCE_GROUPS,
};

export function createTabsBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "tabs",
        data: {
            intro: "",
            items: [createTabItem(), createTabItem(), createTabItem()],
        },
    };
}

export const createLayoutColumn = (): LayoutColumn => ({
    id: uuidv4(),
    blocks: [],
});

export function createColumnsBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "columns",
        data: { columns: [createLayoutColumn(), createLayoutColumn()] },
    };
}

/** No columns-in-columns. Columns can still go into a tab or a step. */
export const canNestIn = (
    type: LomdaV3BlockType,
    containerType: LomdaV3BlockType
): boolean => !(type === "columns" && containerType === "columns");

export const SPACER_HEIGHT = {
    min: 10,
    max: 400,
    step: 10,
    initial: 40,
} as const;

export const clampSpacerHeight = (px: unknown): number =>
    typeof px === "number" && Number.isFinite(px)
        ? Math.min(
              SPACER_HEIGHT.max,
              Math.max(SPACER_HEIGHT.min, Math.round(px))
          )
        : SPACER_HEIGHT.initial;

export function createSpacerBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "spacer",
        data: { height: SPACER_HEIGHT.initial },
    };
}

export const DIVIDER_CONFIG: {
    defaults: { thickness: DividerThickness; spacing: DividerSpacing };
    thicknessClass: Record<DividerThickness, string>;
    spacingScale: Record<DividerSpacing, number>;
    themeColor: string;
} = {
    defaults: { thickness: "thin", spacing: "m" },
    thicknessClass: {
        thin: "border-t",
        medium: "border-t-2",
        thick: "border-t-4",
    },
    spacingScale: { none: 0, s: 0.5, m: 1, l: 2 },
    themeColor: "hsl(var(--lomda-border))",
};

const oneOf = <T extends string>(
    list: readonly T[],
    v: unknown,
    fallback: T
) => (list.includes(v as T) ? (v as T) : fallback);

export const resolveDividerAppearance = (a: DividerAppearance | undefined) => ({
    thickness: oneOf(
        DIVIDER_THICKNESSES,
        a?.thickness,
        DIVIDER_CONFIG.defaults.thickness
    ),
    spacing: oneOf(
        DIVIDER_SPACINGS,
        a?.spacing,
        DIVIDER_CONFIG.defaults.spacing
    ),
    color: isHexColor(a?.color) ? a.color : undefined,
});

export function createDividerBlock(): LomdaV3Block {
    return { id: uuidv4(), type: "divider", data: {} };
}

export const BUTTON_CONFIG: {
    defaults: { style: ButtonStyle; size: ButtonSize; align: ButtonAlign };
    sizeClass: Record<ButtonSize, string>;
    alignClass: Record<ButtonAlign, string>;
    theme: { primary: string; primaryForeground: string };
} = {
    defaults: { style: "filled", size: "m", align: "center" },
    // With the 2px border, M is exactly PreviewButton's height.
    sizeClass: {
        s: "px-[18px] py-1",
        m: "px-[30px] py-2",
        l: "px-[38px] py-3",
    },
    alignClass: {
        start: "justify-start",
        center: "justify-center",
        end: "justify-end",
    },
    theme: {
        primary: "hsl(var(--primary))",
        primaryForeground: "hsl(var(--primary-foreground))",
    },
};

export const resolveButtonAppearance = (a: ButtonAppearance | undefined) => ({
    style: oneOf(BUTTON_STYLES, a?.style, BUTTON_CONFIG.defaults.style),
    size: oneOf(BUTTON_SIZES, a?.size, BUTTON_CONFIG.defaults.size),
    align: oneOf(BUTTON_ALIGNS, a?.align, BUTTON_CONFIG.defaults.align),
    color: isHexColor(a?.color) ? a.color : undefined,
    textColor: isHexColor(a?.textColor) ? a.textColor : undefined,
});

/** The author's colours, else the theme's: Primary, and on top of it Primary
 *  foreground when filled or the button colour when outlined. */
export function buttonColors(
    a: ButtonAppearance | undefined,
    theme: { primary: string; primaryForeground: string }
) {
    const { style, color, textColor } = resolveButtonAppearance(a);
    const fill = color ?? theme.primary;
    return {
        color: fill,
        textColor:
            textColor ?? (style === "filled" ? theme.primaryForeground : fill),
    };
}

export type ButtonTarget =
    | { kind: "url"; href: string }
    | { kind: "page"; pageId: string }
    | { kind: "none" }
    | { kind: "missing-page" };

export function resolveButtonTarget(
    data: ButtonBlock["data"] | undefined,
    pageIds: readonly string[]
): ButtonTarget {
    if (data?.action === "page") {
        if (!data.pageId) return { kind: "none" };
        return pageIds.includes(data.pageId)
            ? { kind: "page", pageId: data.pageId }
            : { kind: "missing-page" };
    }
    const href = normalizeWebUrl(data?.url);
    return href ? { kind: "url", href } : { kind: "none" };
}

export function createButtonBlock(label: string): LomdaV3Block {
    return { id: uuidv4(), type: "button", data: { label, action: "url" } };
}

export const createStepItem = (): StepItem => ({
    id: uuidv4(),
    title: "",
    subtitle: "",
    contentHeading: "",
    blocks: [],
});

/** Resolve a steps block's appearance, defaulting the badge backgrounds + card
 * colors from the theme (brand kit). Number text color also comes from the
 * theme (primary / primaryForeground) — handled in the block. */
export function resolveStepsAppearance(
    a: StepsAppearance | undefined,
    theme: LomdaTheme
): StepsAppearance {
    return {
        cardBackground: a?.cardBackground ?? theme?.card ?? DEFAULT_THEME.card,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
        numberBackground: a?.numberBackground ?? theme.muted,
        activeNumberBackground: a?.activeNumberBackground ?? theme.primary,
    };
}

/** Declarative steps appearance config. Only the badge backgrounds are
 * block-specific; the number text color follows the theme. */
const STEPS_APPEARANCE_GROUPS: AppearanceField[][] = [
    [
        { key: "cardBackground", labelKey: "lomda-v3-editor.steps.appearance-card-bg", control: "color" },
        { key: "border", labelKey: "lomda-v3-editor.steps.appearance-border", control: "color" },
        { key: "borderWidth", labelKey: "lomda-v3-editor.steps.appearance-border-width", control: "borderWidth" },
    ],
    [
        { key: "numberBackground", labelKey: "lomda-v3-editor.steps.appearance-number-bg", control: "color" },
        { key: "activeNumberBackground", labelKey: "lomda-v3-editor.steps.appearance-active-number-bg", control: "color" },
    ],
];
export const STEPS_CONFIG = {
    appearanceGroups: STEPS_APPEARANCE_GROUPS,
};

export function createStepsBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "steps",
        data: {
            intro: "",
            items: [createStepItem(), createStepItem(), createStepItem()],
        },
    };
}

export const createChecklistItem = (): ChecklistItem => ({
    id: uuidv4(),
    text: "",
});

/** Figma-consistent default appearance for a checklist block. */
/** Unset fields follow the theme (card / border). */
export function resolveChecklistAppearance(
    a: ChecklistAppearance | undefined,
    theme: LomdaTheme | undefined
): ChecklistAppearance {
    return {
        cardBackground: a?.cardBackground ?? theme?.card ?? DEFAULT_THEME.card,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
    };
}

/** Declarative checklist appearance config. Mirrors `ACCORDION_CONFIG`. */
const CHECKLIST_APPEARANCE_GROUPS: AppearanceField[][] = [
    [
        { key: "cardBackground", labelKey: "lomda-v3-editor.checklist.appearance-card-bg", control: "color" },
        { key: "border", labelKey: "lomda-v3-editor.checklist.appearance-border", control: "color" },
        { key: "borderWidth", labelKey: "lomda-v3-editor.checklist.appearance-border-width", control: "borderWidth" },
    ],
];
export const CHECKLIST_CONFIG = {
    appearanceGroups: CHECKLIST_APPEARANCE_GROUPS,
};

export function createChecklistBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "checklist",
        data: {
            title: "",
            items: [createChecklistItem(), createChecklistItem(), createChecklistItem()],
        },
    };
}

export const createFlipCard = (icon = ""): FlipCard => ({
    id: uuidv4(),
    icon,
    title: "",
    subtitle: "",
    back: "",
});

/** Figma default appearance for a flip-cards block. */
/** Unset fields follow the theme (Figma: card / background / border). */
export function resolveFlipAppearance(
    a: FlipCardsAppearance | undefined,
    theme: LomdaTheme | undefined
): FlipCardsAppearance {
    return {
        cardBackground: a?.cardBackground ?? theme?.card ?? DEFAULT_THEME.card,
        iconBackground: a?.iconBackground ?? theme?.muted ?? DEFAULT_THEME.muted,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
        showCheckmark: a?.showCheckmark ?? true,
    };
}

export type AppearanceControl =
    | "color"
    | "borderWidth"
    | "switch"
    | "columns"
    | "rows";
export interface AppearanceField {
    key: string;
    labelKey: string;
    control: AppearanceControl;
}
const FLIP_APPEARANCE_GROUPS: AppearanceField[][] = [
    [
        { key: "cardBackground", labelKey: "lomda-v3-editor.flip.appearance-card-bg", control: "color" },
        { key: "iconBackground", labelKey: "lomda-v3-editor.flip.appearance-icon-bg", control: "color" },
        { key: "border", labelKey: "lomda-v3-editor.flip.appearance-border", control: "color" },
        { key: "borderWidth", labelKey: "lomda-v3-editor.flip.appearance-border-width", control: "borderWidth" },
    ],
    [
        { key: "columns", labelKey: "lomda-v3-editor.flip.columns", control: "columns" },
        { key: "showCheckmark", labelKey: "lomda-v3-editor.flip.appearance-checkmark", control: "switch" },
    ],
];
const FLIP_MAX_COLUMNS: Record<FlipCardsVariant, number> = { flip: 6, static: 4 };
export const FLIP_CARDS_CONFIG = {
    defaultColumns: 3,
    minColumns: 1,
    maxColumns: FLIP_MAX_COLUMNS,
    /** A static row drops a card rather than go narrower than this (px). */
    staticMinCardWidth: 200,
    flipOnlyFields: ["showCheckmark"],
    appearanceGroups: FLIP_APPEARANCE_GROUPS,
};

export const flipCardsVariant = (
    data: FlipCardsBlock["data"]
): FlipCardsVariant => data.variant ?? "flip";

export const STATIC_CARDS_LABEL_KEY = "lomda-v3-editor.interactive.static-cards";

export const variantLabelKey = (block: LomdaV3Block): string | undefined =>
    block.type === "flip-cards" && flipCardsVariant(block.data) === "static"
        ? STATIC_CARDS_LABEL_KEY
        : undefined;

export function createFlipCardsBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "flip-cards",
        data: {
            title: "",
            description: "",
            columns: 3,
            cards: [createFlipCard(), createFlipCard(), createFlipCard()],
        },
    };
}

export function createStaticCardsBlock(): LomdaV3Block {
    const block = createFlipCardsBlock() as FlipCardsBlock;
    return { ...block, data: { ...block.data, variant: "static" } };
}

export const createScenarioChoice = (
    quality: ScenarioQuality = "good"
): ScenarioChoice => ({
    id: uuidv4(),
    text: "",
    feedback: "",
    quality,
    nextSceneId: "",
});

export const createScenarioScene = (): ScenarioScene => ({
    id: uuidv4(),
    name: "",
    dialogue: "",
    choices: [
        createScenarioChoice("good"),
        createScenarioChoice("average"),
        createScenarioChoice("poor"),
    ],
});

export function createScenarioBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "scenario",
        data: { scenes: [createScenarioScene()] },
    };
}

export const createSortCategory = (): SortCategory => ({
    id: uuidv4(),
    title: "",
});

export const createSortCard = (categoryId: string): SortCard => ({
    id: uuidv4(),
    text: "",
    categoryId,
});

export function createCardSortingBlock(): LomdaV3Block {
    const catA = createSortCategory();
    const catB = createSortCategory();
    return {
        id: uuidv4(),
        type: "card-sorting",
        data: {
            title: "",
            description: "",
            categories: [catA, catB],
            cards: [createSortCard(catA.id), createSortCard(catB.id)],
        },
    };
}

export const createHowToStep = (): HowToStep => ({
    id: uuidv4(),
    title: "",
    body: "",
    image: "",
});

export function createHowToBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "how-to",
        data: { steps: [createHowToStep(), createHowToStep()] },
    };
}

export const createHotspotMarker = (
    x: number,
    y: number,
    width: number,
    height: number
): HotspotMarker => ({
    id: uuidv4(),
    x,
    y,
    width,
    height,
    label: "",
    color: HOTSPOT_COLORS[0],
    textPosition: "auto",
});

export function createHotspotBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "hotspot",
        data: { image: "", alt: "", aspect: "auto", fit: "cover", markers: [] },
    };
}

export const createQuizOption = (): QuizOption => ({
    id: uuidv4(),
    text: "",
    correct: false,
});

/** Four options for a new choice question; A is correct. */
export const createChoiceOptions = (): QuizOption[] => [
    { ...createQuizOption(), correct: true },
    createQuizOption(),
    createQuizOption(),
    createQuizOption(),
];

export const createQuizQuestion = (): QuizQuestion => ({
    id: uuidv4(),
    text: "",
    multiple: false,
    type: "single",
    explanation: "",
    options: createChoiceOptions(),
});

/** Resolve the question type, falling back to the legacy `multiple` boolean. */
export const quizType = (q: QuizQuestion): QuizQuestionType =>
    q.type ?? (q.multiple ? "multiple" : "single");

/** Unset fields follow the theme (card / border); answer colours pass through unfilled. */
export function resolveQuizAppearance(
    a: QuizAppearance | undefined,
    theme: LomdaTheme | undefined
): ResolvedQuizAppearance {
    return {
        ...a,
        background: a?.background ?? theme?.card ?? DEFAULT_THEME.card,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
    };
}

export function createKnowledgeCheckBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "knowledge-check",
        data: { title: "", description: "", questions: [createQuizQuestion()] },
    };
}

export function createRatingBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "rating",
        data: { question: "", max: 5 },
    };
}

export const createPollOption = (): PollOption => ({
    id: uuidv4(),
    text: "",
});

/** Unset fields follow the theme (card / background / border). */
export function resolvePollAppearance(
    a: PollAppearance | undefined,
    theme: LomdaTheme | undefined
): PollAppearance {
    return {
        cardBackground: a?.cardBackground ?? theme?.card ?? DEFAULT_THEME.card,
        optionBackground:
            a?.optionBackground ?? theme?.muted ?? DEFAULT_THEME.muted,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
    };
}

/** Declarative poll appearance config. Mirrors `CHECKLIST_CONFIG`. */
const POLL_APPEARANCE_GROUPS: AppearanceField[][] = [
    [
        { key: "cardBackground", labelKey: "lomda-v3-editor.poll.appearance-card-bg", control: "color" },
        { key: "optionBackground", labelKey: "lomda-v3-editor.poll.appearance-option-bg", control: "color" },
        { key: "border", labelKey: "lomda-v3-editor.poll.appearance-border", control: "color" },
        { key: "borderWidth", labelKey: "lomda-v3-editor.poll.appearance-border-width", control: "borderWidth" },
    ],
];
export const POLL_CONFIG = {
    appearanceGroups: POLL_APPEARANCE_GROUPS,
};

export function createPollBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "poll",
        data: {
            question: "",
            options: [
                createPollOption(),
                createPollOption(),
                createPollOption(),
            ],
        },
    };
}

export function createWordCloudBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "word-cloud",
        data: { question: "", wordCount: 3 },
    };
}

export function createOpenEndedBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "open-ended",
        data: { question: "", guidance: "" },
    };
}

export function createCompleteSentenceBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "complete-sentence",
        // Start with one empty answer input so authors see where to fill it.
        data: { text: "", answers: [], explanation: "" },
    };
}

// ── chart ──

export const createChartPoint = (): ChartPoint => ({
    id: uuidv4(),
    label: "",
    value: 0,
});

/** The palest shade a ramp reaches: 20% brand over the card surface. Below
 *  that a slice stops reading as the brand colour at all. */
const CHART_RAMP_MIN = 0.2;

/** `steps` shades of one colour fading toward `surface`, strongest first. */
function colorRamp(color: string, surface: string, steps: number): string[] {
    if (steps <= 1) return [color];
    return Array.from({ length: steps }, (_, i) =>
        mixHex(color, surface, 1 - (i / (steps - 1)) * (1 - CHART_RAMP_MIN))
    );
}

/**
 * The chart's colours, derived from the theme — charts need one distinguishable
 * colour PER data point but a brand kit only defines a couple. So the ramp is
 * built to fit `count`: ten points get ten distinct shades, not six that start
 * repeating (two identical slices in a donut are unreadable).
 *
 * Two brand hues alternate when the kit sets a `secondary` distinct from
 * `primary`; otherwise every colour is a shade of `primary`.
 */
export function chartPalette(
    theme: LomdaTheme | undefined,
    count = 6
): string[] {
    const n = Math.max(1, count);
    const primary = theme?.primary ?? DEFAULT_THEME.primary;
    const secondary = theme?.secondary ?? DEFAULT_THEME.secondary ?? primary;
    const surface = theme?.card ?? DEFAULT_THEME.card;

    if (secondary.toLowerCase() === primary.toLowerCase()) {
        return colorRamp(primary, surface, n);
    }
    const first = colorRamp(primary, surface, Math.ceil(n / 2));
    const second = colorRamp(secondary, surface, Math.floor(n / 2));
    return Array.from({ length: n }, (_, i) =>
        i % 2 === 0 ? first[i / 2] : second[(i - 1) / 2]
    );
}

/** The colour a point is drawn in: its own override, else the theme ramp. */
export const chartPointColor = (
    point: ChartPoint,
    index: number,
    palette: string[]
): string => point.color ?? palette[index % palette.length];

/** Unset fields follow the theme (primary / card / border). */
export function resolveChartAppearance(
    a: ChartAppearance | undefined,
    theme: LomdaTheme | undefined
): ChartAppearance {
    return {
        seriesColor:
            a?.seriesColor ?? theme?.primary ?? DEFAULT_THEME.primary,
        cardBackground: a?.cardBackground ?? theme?.card ?? DEFAULT_THEME.card,
        border: a?.border ?? theme?.border ?? DEFAULT_THEME.border,
        borderWidth: a?.borderWidth ?? 1,
        showValues: a?.showValues ?? true,
        horizontal: a?.horizontal ?? false,
    };
}

const CHART_SURFACE_FIELDS: AppearanceField[] = [
    { key: "cardBackground", labelKey: "lomda-v3-editor.chart.appearance-card-bg", control: "color" },
    { key: "border", labelKey: "lomda-v3-editor.chart.appearance-border", control: "color" },
    { key: "borderWidth", labelKey: "lomda-v3-editor.chart.appearance-border-width", control: "borderWidth" },
];

/** Bars and slices are coloured per point; a line is a single path, so it has
 *  one colour instead. Used for the editor's row dots as well as the chart. */
export const chartUsesPointColors = (variant: ChartVariant): boolean =>
    variant !== "line";

/** A donut slice can't be negative — there is no such share of a whole. */
export const clampChartValue = (variant: ChartVariant, value: number): number =>
    variant === "donut" ? Math.max(0, value) : value;

/**
 * Appearance fields depend on the shape: bar/donut colour each point (handled
 * separately, per point), a line has one series colour, and only bars can lie
 * on their side.
 */
export function chartAppearanceGroups(
    variant: ChartVariant
): AppearanceField[][] {
    const toggles: AppearanceField[] = [
        { key: "showValues", labelKey: "lomda-v3-editor.chart.appearance-show-values", control: "switch" },
    ];
    if (variant === "bar") {
        toggles.push({
            key: "horizontal",
            labelKey: "lomda-v3-editor.chart.appearance-horizontal",
            control: "switch",
        });
    }
    const series: AppearanceField[] = chartUsesPointColors(variant)
        ? []
        : [
              {
                  key: "seriesColor",
                  labelKey: "lomda-v3-editor.chart.appearance-series-color",
                  control: "color",
              },
          ];
    return [[...series, ...CHART_SURFACE_FIELDS], toggles];
}

export function createChartBlock(): LomdaV3Block {
    return {
        id: uuidv4(),
        type: "chart",
        data: {
            // One block, three shapes — the author switches shape in the topbar.
            variant: "bar",
            title: "",
            caption: "",
            unit: "",
            points: [createChartPoint(), createChartPoint(), createChartPoint()],
        },
    };
}

// Whether a block has no meaningful content yet — used to show a "click to
// edit" placeholder on the canvas instead of an empty preview.
export function isBlockEmpty(block: LomdaV3Block): boolean {
    switch (block.type) {
        case "text":
            return false;
        case "media-layout":
            return !block.data.cells.some((c) => c.url || c.videoId);
        case "audio":
            return !block.data.url;
        case "embed":
            return !block.data.url;
        case "html":
            return !block.data.html;
        case "attachment":
            return !block.data.url;
        case "walkthrough":
            return block.data.steps.length === 0;
        case "accordion":
            return (
                !block.data.title &&
                !block.data.description &&
                !block.data.items.some((i) => i.title || i.body)
            );
        case "tabs":
            return (
                !block.data.intro &&
                !block.data.items.some(
                    (i) => i.label || (i.blocks?.length ?? 0) > 0
                )
            );
        case "checklist":
            return (
                !block.data.title && !block.data.items.some((i) => i.text)
            );
        case "flip-cards":
            return (
                !block.data.title &&
                !block.data.description &&
                !block.data.cards.some(
                    (c) =>
                        c.title ||
                        c.subtitle ||
                        c.back ||
                        c.front
                )
            );
        case "scenario":
            return !(block.data.scenes ?? []).some(
                (s) => s.dialogue || s.choices.some((c) => c.text)
            );
        case "card-sorting":
            return !block.data.categories.some((c) => c.title);
        case "how-to":
            return !block.data.steps.some((s) => s.title || s.body || s.image);
        case "hotspot":
            return !block.data.image;
        case "knowledge-check":
            return (
                !block.data.title &&
                !block.data.description &&
                !block.data.questions.some((q) => q.text)
            );
        case "steps":
            return (
                !block.data.intro &&
                !block.data.items.some(
                    (i) =>
                        i.title ||
                        i.subtitle ||
                        i.contentHeading ||
                        (i.blocks?.length ?? 0) > 0
                )
            );
        case "columns":
            return block.data.columns.every((c) => c.blocks.length === 0);
        case "rating":
            return !block.data.question;
        case "poll":
            return (
                !block.data.question &&
                !block.data.options.some((o) => o.text)
            );
        case "word-cloud":
            return !block.data.question;
        case "open-ended":
            return !block.data.question;
        case "chart":
            return !block.data.points.some((p) => p.label.trim() || p.value);
        case "complete-sentence":
            return !block.data.text;
        case "spacer":
        case "divider":
        case "button":
            return false;
    }
}

export const INLINE_ADD: { key: string; labelKey: string; create: () => LomdaV3Block }[] = [
    { key: "text", labelKey: "lomda-v3-editor.text-variant.text", create: () => createTextBlock("text") },
    { key: "image", labelKey: "lomda-v3-editor.media-variant.single-image", create: () => createMediaLayout("image", "none", 1) },
    { key: "video", labelKey: "lomda-v3-editor.media-variant.single-video", create: () => createMediaLayout("video", "none", 1) },
    { key: "doc", labelKey: "lomda-v3-editor.media-variant.document", create: createAttachmentBlock },
];

export const TEXT_VARIANT_LABEL: Record<TextVariant, string> = {
    text: "lomda-v3-editor.text-variant.text",
    "heading-text": "lomda-v3-editor.text-variant.heading-text",
    "multi-columns": "lomda-v3-editor.text-variant.multi-columns",
    "two-columns": "lomda-v3-editor.text-variant.multi-columns",
    "three-columns": "lomda-v3-editor.text-variant.multi-columns",
    quote: "lomda-v3-editor.text-variant.quote",
    link: "lomda-v3-editor.text-variant.link",
    table: "lomda-v3-editor.text-variant.table",
};

export const EMBED_PROVIDER_LABEL: Record<EmbedProvider, string> = {
    storage: "lomda-v3-editor.embed-variant.storage",
    "web-link": "lomda-v3-editor.embed-variant.web-link",
    canva: "lomda-v3-editor.embed-variant.canva",
    microsoft: "lomda-v3-editor.embed-variant.microsoft",
    google: "lomda-v3-editor.embed-variant.google",
    surveymonkey: "lomda-v3-editor.embed-variant.surveymonkey",
    vimeo: "lomda-v3-editor.embed-variant.vimeo",
    slideshare: "lomda-v3-editor.embed-variant.slideshare",
    prezi: "lomda-v3-editor.embed-variant.prezi",
    youtube: "lomda-v3-editor.embed-variant.youtube",
    powtoon: "lomda-v3-editor.embed-variant.powtoon",
    h5p: "lomda-v3-editor.embed-variant.h5p",
};

function textOf(d: TextData): string {
    switch (d.variant) {
        case "text":
            return d.html;
        case "heading-text":
            return d.text;
        case "multi-columns":
        case "two-columns":
        case "three-columns":
            return d.columns.map((c) => c.text).filter(Boolean).join("");
        case "quote":
            return d.quote;
        case "link":
            return d.description;
        case "table":
            return "";
    }
}

function headingOf(d: TextData): string {
    switch (d.variant) {
        case "heading-text":
            return d.heading;
        case "multi-columns":
        case "two-columns":
        case "three-columns":
            return d.columns[0]?.heading ?? "";
        default:
            return "";
    }
}

export function convertTextData(d: TextData, target: TextVariant): TextData {
    const text = textOf(d);
    const heading = headingOf(d);
    switch (target) {
        case "text":
            return { variant: "text", html: text };
        case "heading-text":
            return { variant: "heading-text", heading, text };
        case "multi-columns":
        case "two-columns":
        case "three-columns":
            return {
                variant: "multi-columns",
                columns:
                    "columns" in d && d.columns.length
                        ? d.columns
                        : [{ heading, text }, emptyTextColumn()],
            };
        case "quote":
            return {
                variant: "quote",
                quote: text,
                authorName: "",
                authorTitle: "",
                authorPhoto: "",
                showAuthorPhoto: true,
                align: "start",
            };
        case "link":
            return {
                variant: "link",
                url: "",
                title: "",
                description: text,
                thumbnail: "",
            };
        case "table":
            return {
                variant: "table",
                rows: defaultTableRows(),
                headerRow: true,
            };
    }
}
